import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../api";
import { toast } from "sonner";
import { ArrowLeft, User, Mail, Phone, FileText, Star, ShoppingCart, Calendar } from "lucide-react";

const ClientDetailPage = () => {
  const { clientId } = useParams();
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadClient();
  }, [clientId]);

  const loadClient = async () => {
    try {
      const res = await api.get(`/clients/${clientId}`);
      setClient(res.data);
    } catch {
      toast.error("Blad ladowania klienta");
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="text-center py-20 text-slate-500">Ladowanie...</div>;
  if (!client) return <div className="text-center py-20 text-slate-500">Klient nie znaleziony</div>;

  return (
    <div className="max-w-4xl mx-auto">
      <Link to="/clients" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-indigo-600 mb-4">
        <ArrowLeft className="w-4 h-4" /> Powrot do listy
      </Link>

      {/* Client info */}
      <div className="bg-white rounded-xl border border-slate-200 p-6 mb-6">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <User className="w-6 h-6" /> {client.name}
            </h1>
            <div className="mt-2 space-y-1">
              <p className="text-sm text-slate-500 flex items-center gap-2">
                <Mail className="w-4 h-4" /> {client.email}
              </p>
              {client.phone && (
                <p className="text-sm text-slate-500 flex items-center gap-2">
                  <Phone className="w-4 h-4" /> {client.phone}
                </p>
              )}
              <p className="text-sm text-slate-500 flex items-center gap-2">
                <Calendar className="w-4 h-4" /> Dodany: {new Date(client.created_at).toLocaleDateString("pl-PL")}
              </p>
            </div>
          </div>
          <div className="flex gap-4 text-center">
            <div className="bg-pink-50 px-4 py-2 rounded-lg">
              <p className="text-2xl font-bold text-pink-600">{client.selection_count || 0}</p>
              <p className="text-xs text-pink-500">Selekcje</p>
            </div>
            <div className="bg-indigo-50 px-4 py-2 rounded-lg">
              <p className="text-2xl font-bold text-indigo-600">{client.order_count || 0}</p>
              <p className="text-xs text-indigo-500">Zamowienia</p>
            </div>
          </div>
        </div>
        {client.notes && (
          <div className="mt-4 bg-slate-50 rounded-lg p-3 flex items-start gap-2 text-sm text-slate-600">
            <FileText className="w-4 h-4 mt-0.5 flex-shrink-0" /> {client.notes}
          </div>
        )}
      </div>

      {/* Selections */}
      <div className="mb-6">
        <h2 className="text-lg font-semibold text-slate-900 mb-3 flex items-center gap-2">
          <Star className="w-5 h-5 text-pink-500" /> Selekcje
        </h2>
        {client.selections?.length > 0 ? (
          <div className="space-y-2">
            {client.selections.map((sel) => (
              <div key={sel.id} className="bg-white rounded-lg border border-slate-200 p-4 flex items-center justify-between">
                <div>
                  <p className="font-medium text-slate-900">{sel.selected_photo_ids?.length || 0} zdjec</p>
                  <p className="text-sm text-slate-500">{new Date(sel.created_at).toLocaleDateString("pl-PL")}</p>
                </div>
                <span
                  className={`px-2 py-1 rounded-full text-xs font-medium ${
                    sel.status === "approved"
                      ? "bg-green-100 text-green-700"
                      : sel.status === "reviewed"
                      ? "bg-blue-100 text-blue-700"
                      : "bg-amber-100 text-amber-700"
                  }`}
                >
                  {sel.status === "approved" ? "Zatwierdzona" : sel.status === "reviewed" ? "Przejrzana" : "Przeslana"}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-slate-400">Brak selekcji</p>
        )}
      </div>

      {/* Orders */}
      <div>
        <h2 className="text-lg font-semibold text-slate-900 mb-3 flex items-center gap-2">
          <ShoppingCart className="w-5 h-5 text-indigo-500" /> Zamowienia
        </h2>
        {client.orders?.length > 0 ? (
          <div className="space-y-2">
            {client.orders.map((order) => (
              <div key={order.id} className="bg-white rounded-lg border border-slate-200 p-4 flex items-center justify-between">
                <div>
                  <p className="font-medium text-slate-900">{order.order_number}</p>
                  <p className="text-sm text-slate-500">{new Date(order.created_at).toLocaleDateString("pl-PL")}</p>
                </div>
                <div className="text-right">
                  <p className="font-medium text-slate-900">{order.total_amount?.toFixed(2)} PLN</p>
                  <span
                    className={`px-2 py-1 rounded-full text-xs font-medium ${
                      order.status === "paid"
                        ? "bg-green-100 text-green-700"
                        : order.status === "delivered"
                        ? "bg-blue-100 text-blue-700"
                        : order.status === "cancelled"
                        ? "bg-red-100 text-red-700"
                        : "bg-amber-100 text-amber-700"
                    }`}
                  >
                    {order.status === "paid" ? "Oplacone" : order.status === "delivered" ? "Dostarczone" : order.status === "cancelled" ? "Anulowane" : "Oczekujace"}
                  </span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-slate-400">Brak zamowien</p>
        )}
      </div>
    </div>
  );
};

export default ClientDetailPage;
